import { API } from '@/src/api/api'
import { useProductAnalytics } from '@/src/api/data/analytics'
import type { EmberDoneEvent } from '@/src/api/types/generated-types'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { type EmberThreadForAgent, resetEmberThread, useEmberThread } from '@/src/hooks/useEmberThread.hook'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { type Dispatch, type SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEmberMessagesStore } from '../ember-messages.store'
import type { IEmberMessage } from '../types'
import { type MessageHandlingCustomActions, useEmberBaseMessageHandling } from './useEmberBaseMessageHandling.hook'

type GeneralRequestData = {
  question: string
  userId: string
  locationId?: string
  buildingId?: number
}
type OnSend<T> = (threadId: string, data: GeneralRequestData) => Promise<T>

type SendMessageHandler = (threadId: string, question?: string) => Promise<void>

type CustomActions = {
  onBeforeSend?: (data: GeneralRequestData) => void
  onAfterSend?: () => void
  onError?: () => void
}

type EmberSetup = {
  handleSendMessage: SendMessageHandler
  cancelExecution: (threadId: string) => void
  isLoading: boolean
  setIsLoading: Dispatch<SetStateAction<boolean>>
  questionInput: string
  setQuestionInput: Dispatch<SetStateAction<string>>
  thinkingExplanation: string | null
  setThinkingExplanation: Dispatch<SetStateAction<string | null>>
}

const cancelExecution = (threadId: string) => {
  void API.ember.cancel({ requestBody: { threadId } })
}

const useEmberSetup = <T>(onSend: OnSend<T>, customActions: CustomActions = {}): EmberSetup => {
  const { t } = useTranslation()
  const { reportEvent } = useProductAnalytics()

  const locations = useLocationsContext()
  const session = useSessionStore()
  const userId = session.user?.id

  // Local component state (everything except messages, proposed questions, and proposed user action)
  const [isLoading, setIsLoading] = useState(false)
  const [questionInput, setQuestionInput] = useState('')
  const [thinkingExplanation, setThinkingExplanation] = useState<string | null>(null)

  const handleSendMessage = async (threadId: string, question?: string) => {
    if (!userId) {
      console.error('Ember access without a userId!')
      return
    }

    if (!questionInput && !question) return

    const questionToSend = question || questionInput

    const userMessage: IEmberMessage = {
      role: 'user',
      displayValue: questionToSend,
    }

    // Use store action for messages
    useEmberMessagesStore.getState().startNewConversation(threadId, userMessage)
    setQuestionInput('')
    setIsLoading(true)

    const generalRequestData: GeneralRequestData = {
      userId,
      locationId: locations.selectedLocationIndex > 0 ? locations?.currentLocation?.id : undefined, // TODO: this is a legacy compatibility kind of thing. We should replace it with locationId eventually (requires backend adjustments)
      buildingId: locations.getHierarchySelection().buildingId,
      question: questionToSend,
    }

    customActions.onBeforeSend?.(generalRequestData)

    try {
      reportEvent('ember_message', { question: questionToSend })
      void onSend(threadId, generalRequestData)
      customActions.onAfterSend?.()
    } catch (e) {
      console.error('Error sending message:', e)
      useEmberMessagesStore.getState().handleErrorMessage(threadId, t('emberChat.failed'))
      setThinkingExplanation(null)
      customActions.onError?.()
    } finally {
      setIsLoading(false)
    }
  }

  return {
    handleSendMessage,
    cancelExecution,
    isLoading,
    setIsLoading,
    questionInput,
    setQuestionInput,
    thinkingExplanation,
    setThinkingExplanation,
  }
}

export const useEmberThreadScopeSetup = <T, D extends EmberDoneEvent>(
  agentType: EmberThreadForAgent,
  onSend: OnSend<T>,
  sendingCustomActions: CustomActions = {},
  messageHandlingCustomActions: MessageHandlingCustomActions<D> = {}
) => {
  const threadId = useEmberThread(agentType)
  const messages = useEmberMessagesStore(state => state.messages[threadId])
  const setup = useEmberSetup(onSend, sendingCustomActions)
  useEmberBaseMessageHandling(threadId, messageHandlingCustomActions)

  return {
    threadId,
    messages,
    ...setup,
    handleSendMessage: (question?: string) => setup.handleSendMessage(threadId, question),
    cancelExecution: () => setup.cancelExecution(threadId),
    updateLastMessage: (updates: Partial<IEmberMessage>) => useEmberMessagesStore.getState().updateLastMessage(threadId, updates),
    clearThread: () => {
      useEmberMessagesStore.getState().clearMessages(threadId)
      resetEmberThread(agentType)
    },
  }
}
